import { useId, useState } from "react"

import { Input } from "@coval/ui/input"

import { JsonCodeEditor } from "@/components/JsonCodeEditor"
import { FieldLabel } from "@/components/shared/forms/FieldLabel"

interface BaseFormFieldProps {
  disabled?: boolean
  className?: string
}

/**
 * Info-tooltip copy for outbound voice agent arguments. Mirrors
 * INBOUND_VOICE_TOOLTIPS so both voice agent forms read consistently.
 */
const OUTBOUND_VOICE_TOOLTIPS = {
  triggerCallEndpoint:
    "The HTTPS webhook Coval calls to kick off an outbound call with your agent. It must handle webhook requests from the voice system.",
  triggerCallHeaders:
    "Optional HTTP headers sent with the trigger-call request as valid JSON, e.g. an Authorization bearer token.",
  phoneNumberKey:
    'The payload key Coval uses to pass the phone number to your endpoint. Defaults to "phone_number".',
  triggerCallPayload:
    "Optional valid JSON body sent with the trigger-call request, e.g. a sequence code or campaign id.",
} as const

interface TriggerCallConfig {
  trigger_call_endpoint: string
  trigger_call_headers: string
  trigger_call_payload: string
  phone_number_key?: string
}

interface OutboundCallConfigFieldsProps extends BaseFormFieldProps {
  config: TriggerCallConfig
  onConfigChange: (config: TriggerCallConfig) => void
  fieldGroup?: "all" | "primary" | "advanced"
}

export const OutboundCallConfigFields: React.FC<
  OutboundCallConfigFieldsProps
> = ({
  config,
  onConfigChange,
  disabled,
  className = "",
  fieldGroup = "all",
}) => {
  const [headersError, setHeadersError] = useState<string | null>(null)
  const [payloadError, setPayloadError] = useState<string | null>(null)

  const endpointLabelId = useId()
  const endpointInputId = useId()
  const headersLabelId = useId()
  const phoneKeyLabelId = useId()
  const phoneKeyInputId = useId()
  const payloadLabelId = useId()

  const handleEndpointChange = (value: string) => {
    onConfigChange({
      ...config,
      trigger_call_endpoint: value,
    })
  }

  const handleHeadersChange = (value: string) => {
    if (value.trim() === "") {
      setHeadersError(null)
      onConfigChange({ ...config, trigger_call_headers: value })
      return
    }
    try {
      JSON.parse(value)
      setHeadersError(null)
    } catch {
      setHeadersError("Invalid JSON format")
    }
    onConfigChange({ ...config, trigger_call_headers: value })
  }

  const handlePayloadChange = (value: string) => {
    if (value.trim() === "") {
      setPayloadError(null)
      onConfigChange({ ...config, trigger_call_payload: value })
      return
    }
    try {
      JSON.parse(value)
      setPayloadError(null)
    } catch {
      setPayloadError("Invalid JSON format")
    }
    onConfigChange({ ...config, trigger_call_payload: value })
  }

  const handlePhoneNumberKeyChange = (value: string) => {
    onConfigChange({ ...config, phone_number_key: value })
  }

  return (
    <div className="space-y-4">
      {fieldGroup !== "advanced" && (
        <div className="space-y-2">
          <FieldLabel
            id={endpointLabelId}
            htmlFor={endpointInputId}
            required
            tooltip={OUTBOUND_VOICE_TOOLTIPS.triggerCallEndpoint}
          >
            Trigger Call Endpoint
          </FieldLabel>
          <Input
            id={endpointInputId}
            value={config.trigger_call_endpoint}
            placeholder="Enter your trigger call endpoint"
            onChange={(e) => handleEndpointChange(e.target.value)}
            className={className}
            disabled={disabled}
          />
        </div>
      )}

      {fieldGroup !== "primary" && (
        <>
          <div className="space-y-2">
            <FieldLabel
              id={headersLabelId}
              tooltip={OUTBOUND_VOICE_TOOLTIPS.triggerCallHeaders}
            >
              Trigger Call Headers
            </FieldLabel>
            <JsonCodeEditor
              hideGutters
              copyable
              value={config.trigger_call_headers}
              onChange={handleHeadersChange}
              className={className}
              disabled={disabled}
              rows={3}
              error={headersError}
              ariaLabelledBy={headersLabelId}
              ariaLabel="Trigger call headers"
            />
          </div>

          <div className="space-y-2">
            <FieldLabel
              id={phoneKeyLabelId}
              htmlFor={phoneKeyInputId}
              tooltip={OUTBOUND_VOICE_TOOLTIPS.phoneNumberKey}
            >
              Phone Number Key
            </FieldLabel>
            <Input
              id={phoneKeyInputId}
              value={config.phone_number_key || ""}
              placeholder="Enter the phone number payload key"
              onChange={(e) => handlePhoneNumberKeyChange(e.target.value)}
              className={className}
              disabled={disabled}
            />
          </div>

          <div className="space-y-2">
            <FieldLabel
              id={payloadLabelId}
              tooltip={OUTBOUND_VOICE_TOOLTIPS.triggerCallPayload}
            >
              Trigger Call Payload
            </FieldLabel>
            <JsonCodeEditor
              hideGutters
              copyable
              value={config.trigger_call_payload}
              onChange={handlePayloadChange}
              className={className}
              disabled={disabled}
              rows={3}
              error={payloadError}
              ariaLabelledBy={payloadLabelId}
              ariaLabel="Trigger call payload"
            />
          </div>
        </>
      )}
    </div>
  )
}
